import { getErrorMessage } from '@/helpers/auth';
import { t } from '@/i18n';
import AppMessages from '@/models/core/AppMessages';
import { supabase } from '@/supabase';
import type { ServiceResponse } from '@/types/core';
import type { ForemanResponse, ForemanSearchFilters, ForemanViewResponse } from '@/types/foreman';
import { SortOrders } from '@/types/list';

/**
 * Creates a new foreman
 *
 * @param foreman The foreman data to create
 * @returns ServiceResponse with the created foreman UID or error
 */
export async function createForeman(foreman: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyUid: string;
  active: boolean;
}): Promise<ServiceResponse<string>> {
  try {
    const { data, error } = await supabase.rpc('create_foreman', {
      p_active: foreman.active,
      p_company_uid: foreman.companyUid,
      p_email: foreman.email,
      p_first_name: foreman.firstName,
      p_last_name: foreman.lastName,
      p_phone: foreman.phone,
    });

    if (error) {
      // Extract the standardized error code from the message
      const errorCode = error.message;

      // Translate the error code to a user-friendly message
      const localizedError = t(`errors.foreman.${errorCode}`);

      // Display the error to the user
      AppMessages.error(localizedError);

      return { data: null, error: localizedError };
    }

    // Return the created foreman UID
    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Updates an existing foreman
 *
 * @param uid The UID of the foreman to update
 * @param foreman The updated foreman data
 * @returns ServiceResponse with the updated foreman UID or error
 */
export async function updateForeman(
  uid: string,
  foreman: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    companyUid: string;
    active: boolean;
  },
): Promise<ServiceResponse<string>> {
  try {
    const { data, error } = await supabase.rpc('update_foreman', {
      p_active: foreman.active,
      p_company_uid: foreman.companyUid,
      p_email: foreman.email,
      p_first_name: foreman.firstName,
      p_foreman_uid: uid,
      p_last_name: foreman.lastName,
      p_phone: foreman.phone,
    });

    if (error) {
      console.error('Foreman update error:', error);

      // Extract the standardized error code from the message
      const errorCode = error.message;

      // Translate the error code to a user-friendly message
      const localizedError = t(`errors.foreman.${errorCode}`);

      // Display the error to the user
      AppMessages.error(localizedError);

      return { data: null, error: localizedError };
    }

    // Return the updated foreman UID
    return { data, error: null };
  } catch (err: unknown) {
    console.error('Foreman update exception:', err);
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Gets all foremen
 * @returns ServiceResponse with foremen data or error
 */
export async function getAll(): Promise<ServiceResponse<ForemanResponse[]>> {
  try {
    const { data, error: err } = await supabase.from('foremans').select('*').is('deleted_at', null).eq('active', true).order('first_name', { ascending: true });

    if (err) {
      const error = t(`errors.foreman.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data: data as ForemanResponse[], error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Get foreman by uid
 * @returns ServiceResponse with single foreman data or error
 */
export async function getByUid(uid: string): Promise<ServiceResponse<ForemanResponse>> {
  try {
    const { data, error: err } = await supabase.from('foremans').select('*').eq('uid', uid).is('deleted_at', null).single();

    if (err) {
      const error = t(`errors.foreman.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Deletes a foreman by UID (soft delete)
 * @param uid The UID of the foreman to delete
 * @returns ServiceResponse with the deleted foreman UID or error
 */
export async function deleteByUid(uid: string): Promise<ServiceResponse<string>> {
  try {
    const { data, error: err } = await supabase.rpc('delete_foreman', { p_foreman_uid: uid });

    if (err) {
      const error = t(`errors.foreman.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}

/**
 * Searches foremen with advanced filtering, sorting and pagination
 * @param filters ForemanSearchFilters object with filtering options
 * @returns ServiceResponse with foremen data and total count or error
 */
export async function search(filters: ForemanSearchFilters = {}): Promise<ServiceResponse<{ foremans: ForemanViewResponse[]; total: number }>> {
  try {
    const { textFilter = '', includeInactive, companyUid, pageNumber = 1, pageSize = 10, sortBy = 'last_name', sortOrder = SortOrders.ASC } = filters;

    // Build the query using the foremans_list_view for better sorting performance
    let query = supabase.from('foremans_list_view').select('*', { count: 'exact' }).is('deleted_at', null);

    // Apply status filter
    if (!includeInactive) {
      query = query.eq('active', true);
    }

    // Apply company filter
    if (companyUid) {
      query = query.eq('company_uid', companyUid);
    }

    // Apply text search filter
    if (textFilter) {
      const searchValue = `%${textFilter}%`;
      query = query.or(`first_name.ilike.${searchValue},last_name.ilike.${searchValue},email.ilike.${searchValue}`);
    }

    // Apply sorting
    const ascending = sortOrder === SortOrders.ASC;
    query = query.order(sortBy, { ascending });

    // Apply pagination
    const from = (pageNumber - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error: err, count } = await query;

    if (err) {
      const error = t(`errors.foreman.${err.message}`);
      AppMessages.error(error);
      return { data: null, error };
    }

    // Data is already properly formatted from the view
    const transformedData = (data || []) as ForemanViewResponse[];

    return {
      data: {
        foremans: transformedData,
        total: count || 0,
      },
      error: null,
    };
  } catch (err: unknown) {
    const error = getErrorMessage(err);
    AppMessages.error(error);
    return { data: null, error };
  }
}
